import { ReactElement, useEffect, useId, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectRoles } from 'src/v3/settings/settings.slice'
import {
  clearNotificationPreferencesAlert,
  fetchNotificationPreferences,
  selectNotificationPreferences,
  selectNotificationPreferencesAlert,
  selectNotificationPreferencesStatus,
  updateNotificationPreferences,
} from 'src/v3/profile/profile.slice'

import BasePage from '../layout'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let roles = useAppSelector(selectRoles)
  let preferences = useAppSelector(selectNotificationPreferences)
  let preferencesStatus = useAppSelector(selectNotificationPreferencesStatus)
  let alert = useAppSelector(selectNotificationPreferencesAlert)
  let assignmentDescId = useId()
  let credentialDescId = useId()
  let managerDescId = useId()
  let scheduledAssignmentDescId = useId()

  let isManager = roles?.includes('manager')
  let isLoading = preferencesStatus === 'pending'
  let showScheduledAssignmentReminder = isManager

  let [reminderDays, setReminderDays] = useState<number>(
    preferences?.scheduledAssignmentReminderDays ?? 7,
  )

  useEffect(() => {
    if (preferencesStatus === 'uninitialized') {
      dispatch(fetchNotificationPreferences())
    }
  }, [preferencesStatus, dispatch])

  useEffect(() => {
    if (preferences?.scheduledAssignmentReminderDays !== undefined) {
      setReminderDays(preferences.scheduledAssignmentReminderDays)
    }
  }, [preferences?.scheduledAssignmentReminderDays])

  let handleToggle = (
    field:
      | 'assignmentsDueDigest'
      | 'credentialsExpiringDigest'
      | 'managerDigest'
      | 'scheduledAssignmentReminder',
  ) => {
    if (!preferences || isLoading) return

    if (field === 'scheduledAssignmentReminder') {
      let isEnabled = preferences.scheduledAssignmentReminderDays !== undefined
      dispatch(
        updateNotificationPreferences({
          credentialsExpiringDigest: preferences.credentialsExpiringDigest,
          assignmentsDueDigest: preferences.assignmentsDueDigest,
          managerDigest: preferences.managerDigest,
          scheduledAssignmentReminderDays: isEnabled ? undefined : reminderDays,
        }),
      )
    } else {
      let newValue = !preferences[field]

      dispatch(
        updateNotificationPreferences({
          credentialsExpiringDigest: preferences.credentialsExpiringDigest,
          assignmentsDueDigest: preferences.assignmentsDueDigest,
          managerDigest: preferences.managerDigest,
          scheduledAssignmentReminderDays:
            preferences.scheduledAssignmentReminderDays,
          [field]: newValue,
        }),
      )
    }
  }

  let handleReminderDaysChange = (value: string) => {
    let numValue = parseInt(value, 10)
    if (Number.isNaN(numValue) || numValue < 1) return

    setReminderDays(numValue)
  }

  let handleReminderDaysBlur = () => {
    if (!preferences || isLoading) return
    if (preferences.scheduledAssignmentReminderDays === undefined) return

    dispatch(
      updateNotificationPreferences({
        credentialsExpiringDigest: preferences.credentialsExpiringDigest,
        assignmentsDueDigest: preferences.assignmentsDueDigest,
        managerDigest: preferences.managerDigest,
        scheduledAssignmentReminderDays: reminderDays,
      }),
    )
  }

  let handleCloseAlert = () => {
    dispatch(clearNotificationPreferencesAlert())
  }

  return (
    <BasePage>
      <Box component="main" className="flex flex-col gap-6">
        <Box component="header">
          <Typography
            variant="h5"
            component="h2"
            className="mb-2"
            id="preferences-heading"
          >
            Email Notification Preferences
          </Typography>
          <Typography
            color="text.secondary"
            variant="body2"
            id="preferences-description"
          >
            Configure when you receive email notifications
          </Typography>
        </Box>

        {/* Loading state announcement for screen readers */}
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {isLoading && 'Updating preferences'}
        </Box>

        <Card
          variant="outlined"
          className="p-6"
          role="group"
          aria-labelledby="preferences-heading"
          aria-describedby="preferences-description"
        >
          <FormGroup className="flex flex-col gap-6">
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={preferences?.assignmentsDueDigest ?? true}
                    onChange={() => handleToggle('assignmentsDueDigest')}
                    disabled={isLoading}
                    tabIndex={0}
                    sx={{
                      '&.Mui-focusVisible .MuiSwitch-thumb': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px',
                      },
                    }}
                    slotProps={{
                      input: {
                        'aria-label': 'Assignment Due Digest',
                        'aria-describedby': assignmentDescId,
                        role: 'switch',
                        'aria-checked':
                          preferences?.assignmentsDueDigest ?? true,
                        tabIndex: 0,
                      },
                    }}
                  />
                }
                label={
                  <Box component="span">
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'medium',
                      }}
                    >
                      Assignment Due Digest
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      id={assignmentDescId}
                    >
                      Receive a daily summary of your upcoming and past due
                      assignments
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
                className="items-start m-0"
                sx={{ alignItems: 'flex-start' }}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={preferences?.credentialsExpiringDigest ?? true}
                    onChange={() => handleToggle('credentialsExpiringDigest')}
                    disabled={isLoading}
                    tabIndex={0}
                    sx={{
                      '&.Mui-focusVisible .MuiSwitch-thumb': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px',
                      },
                    }}
                    slotProps={{
                      input: {
                        'aria-label': 'Credential Expiration Digest',
                        'aria-describedby': credentialDescId,
                        role: 'switch',
                        'aria-checked':
                          preferences?.credentialsExpiringDigest ?? true,
                        tabIndex: 0,
                      },
                    }}
                  />
                }
                label={
                  <Box component="span">
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'medium',
                      }}
                    >
                      Credential Expiration Digest
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      id={credentialDescId}
                    >
                      Receive a daily summary of your upcoming credential
                      expirations
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
                className="items-start m-0"
                sx={{ alignItems: 'flex-start' }}
              />
            </Box>

            {isManager && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={preferences?.managerDigest ?? true}
                      onChange={() => handleToggle('managerDigest')}
                      disabled={isLoading}
                      tabIndex={0}
                      sx={{
                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '2px',
                        },
                      }}
                      slotProps={{
                        input: {
                          'aria-label': 'Manager Digest',
                          'aria-describedby': managerDescId,
                          role: 'switch',
                          'aria-checked': preferences?.managerDigest ?? true,
                          tabIndex: 0,
                        },
                      }}
                    />
                  }
                  label={
                    <Box component="span">
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'medium',
                        }}
                      >
                        Manager Digest
                      </Typography>
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        id={managerDescId}
                      >
                        Receive a weekly digest of all your organization's
                        users' assignments and credentials
                      </Typography>
                    </Box>
                  }
                  labelPlacement="end"
                  className="items-start m-0"
                  sx={{ alignItems: 'flex-start' }}
                />
              </Box>
            )}

            {showScheduledAssignmentReminder && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={
                        preferences?.scheduledAssignmentReminderDays !==
                        undefined
                      }
                      onChange={() =>
                        handleToggle('scheduledAssignmentReminder')
                      }
                      disabled={isLoading}
                      tabIndex={0}
                      sx={{
                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '2px',
                        },
                      }}
                      slotProps={{
                        input: {
                          'aria-label': 'Scheduled Assignment Reminder',
                          'aria-describedby': scheduledAssignmentDescId,
                          role: 'switch',
                          'aria-checked':
                            preferences?.scheduledAssignmentReminderDays !==
                            undefined,
                          tabIndex: 0,
                        },
                      }}
                    />
                  }
                  label={
                    <Box component="span">
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'medium',
                        }}
                      >
                        Scheduled Assignment Reminder
                      </Typography>
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        id={scheduledAssignmentDescId}
                      >
                        Receive a reminder email before a scheduled assignment
                        is automatically sent to users
                      </Typography>
                    </Box>
                  }
                  labelPlacement="end"
                  className="items-start m-0"
                  sx={{ alignItems: 'flex-start' }}
                />
                {preferences?.scheduledAssignmentReminderDays !== undefined && (
                  <Box className="mt-3 ml-14">
                    <TextField
                      type="number"
                      label="Days before scheduled date"
                      value={reminderDays}
                      onChange={(e) => handleReminderDaysChange(e.target.value)}
                      onBlur={handleReminderDaysBlur}
                      disabled={isLoading}
                      size="small"
                      required
                      slotProps={{
                        htmlInput: {
                          min: 1,
                          max: 30,
                          'aria-label':
                            'Number of days before scheduled date to receive notification',
                          'aria-required': 'true',
                          'aria-valuemin': 1,
                          'aria-valuemax': 30,
                          'aria-valuenow': reminderDays,
                        },
                        input: {
                          sx: {
                            bgcolor: 'background.paperDark',
                            '&.Mui-focused': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: '2px',
                            },
                          },
                        },
                      }}
                      helperText="How many days before the scheduled assignment date should you be notified (1-30 days)"
                      className="w-64"
                    />
                  </Box>
                )}
              </Box>
            )}
          </FormGroup>
        </Card>
      </Box>
      <Snackbar
        open={!!alert}
        onClose={handleCloseAlert}
        autoHideDuration={alert?.type !== 'error' ? 3000 : undefined}
        aria-live="polite"
        aria-atomic="true"
      >
        <Alert
          variant="filled"
          onClose={handleCloseAlert}
          severity={alert?.type}
          role="alert"
          aria-live={alert?.type === 'error' ? 'assertive' : 'polite'}
        >
          {alert?.message}
        </Alert>
      </Snackbar>
    </BasePage>
  )
}
